import { useEffect, useMemo, useRef, useState } from "react";
import type { FlatEvent, Level, LevelReason } from "@backstory/protocol";
import { LEVELS } from "@backstory/protocol";
import { LEVEL_LABEL, REASON_LABEL } from "../lib/capture";
import { formatClock } from "../lib/format";

const isLevel = (v: unknown): v is Level => typeof v === "string" && (LEVELS as readonly string[]).includes(v);

export interface EventListProps {
  events: FlatEvent[];
  startTs: number;
  /** Playhead, relative ms. */
  time: number;
  onSeek: (relMs: number) => void;
}

const FILTERS: Array<{ key: string; label: string; match: (e: FlatEvent) => boolean; color: string }> = [
  { key: "click", label: "Clicks", match: (e) => e.type === "pointer" && e.subtype === "click", color: "var(--accent)" },
  { key: "navigation", label: "Pages", match: (e) => e.type === "navigation", color: "var(--info)" },
  { key: "error", label: "Errors", match: (e) => e.type === "error", color: "var(--danger)" },
  { key: "console", label: "Console", match: (e) => e.type === "console", color: "var(--warn)" },
  { key: "network", label: "Network", match: (e) => e.type === "network", color: "var(--fg-subtle)" },
  { key: "input", label: "Inputs", match: (e) => e.type === "input", color: "var(--ok)" },
  // Off by default: application state is detailed in DevTools, but this filter
  // makes an action searchable beside the moment that caused it.
  { key: "state", label: "State", match: (e) => e.type === "state", color: "var(--accent)" },
  // Off by default: recorder bookkeeping is noise until you are asking why a
  // stretch of the replay looks thin, and then it is the only thing that answers.
  { key: "capture", label: "Capture", match: (e) => e.type === "control" && (e.subtype === "level" || e.subtype === "health" || e.subtype === "gap"), color: "var(--info)" },
];

export function EventList({ events, startTs, time, onSeek }: EventListProps) {
  const [active, setActive] = useState<Set<string>>(() => new Set(["click", "navigation", "error", "console", "network", "input"]));
  const listRef = useRef<HTMLUListElement>(null);

  const visible = useMemo(
    () => events.filter((e) => FILTERS.some((f) => active.has(f.key) && f.match(e))).sort((a, b) => a.ts - b.ts),
    [events, active],
  );

  // Current = last event at or before the playhead.
  const currentIdx = useMemo(() => {
    const abs = startTs + time;
    let idx = -1;
    for (let i = 0; i < visible.length; i++) {
      if (visible[i]!.ts <= abs) idx = i;
      else break;
    }
    return idx;
  }, [visible, startTs, time]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('li[aria-current="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [currentIdx]);

  const toggle = (key: string) => {
    const next = new Set(active);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setActive(next);
  };

  return (
    <section className="events card" aria-label="Events">
      <header>
        <h2>Events ({visible.length})</h2>
        <div className="filters">
          {FILTERS.map((f) => (
            <button key={f.key} aria-pressed={active.has(f.key)} onClick={() => toggle(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      </header>
      <ul ref={listRef} className="scroll-cue">
        {visible.length === 0 && <li className="muted">No events match the current filters.</li>}
        {visible.map((e, i) => {
          const f = FILTERS.find((x) => x.match(e));
          return (
            <li key={`${e.seq}-${e.idx}-${i}`} aria-current={i === currentIdx ? "true" : undefined} onClick={() => onSeek(e.ts - startTs)}>
              <span className="t">{formatClock(e.ts - startTs)}</span>
              <span className="label">
                <span className="icon" style={{ background: f?.color ?? "var(--fg-subtle)" }} />
                <span className="detail" title={describe(e)}>
                  {describe(e)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function describe(e: FlatEvent): string {
  switch (e.type) {
    case "pointer":
      return `Click ${e.name ?? (e.nodeId ? `on node #${e.nodeId}` : "")}`.trim();
    case "navigation":
      return `Navigate → ${e.url ?? ""}`;
    case "error":
      return `Error: ${e.value ?? e.name ?? "unknown"}`;
    case "console": {
      const text = e.value ?? "";
      if (text.startsWith("Live Assist ended")) return text;
      return `console.${e.subtype ?? "log"}: ${text}`;
    }
    case "network":
      return `${e.name ?? "GET"} ${e.url ?? ""} → ${e.status ?? "…"}${e.durationMs ? ` (${Math.round(e.durationMs)} ms)` : ""}`;
    case "input":
      return `Input on node #${e.nodeId ?? "?"}${e.value ? `: ${e.value}` : ""}`;
    case "custom":
      return `${e.subtype ?? "info"} · ${e.name ?? ""}`;
    case "state":
      return `${e.subtype ?? "update"} · ${e.name ?? e.value ?? "state"}`;
    case "control":
      return describeControl(e);
    default:
      return `${e.type}${e.subtype ? `.${e.subtype}` : ""}`;
  }
}

/**
 * Recorder bookkeeping, in the reader's terms. These events explain why a
 * stretch of replay is thinner than the rest, which is worth a sentence rather
 * than the raw `control.level` the default branch would print.
 */
function describeControl(e: FlatEvent): string {
  const level = isLevel(e.name) ? LEVEL_LABEL[e.name] : e.name;
  switch (e.subtype) {
    case "level": {
      const why = e.value && e.value in REASON_LABEL ? REASON_LABEL[e.value as LevelReason] : undefined;
      // Recordings made before the recorder reported a reason still say which
      // way they moved, because reaching Healthy can only be a recovery.
      const recovered = e.value === "recovery" || (!e.value && e.name === "HEALTHY");
      return recovered ? `Capture recovered to ${level}` : `Capture reduced to ${level}${why ? ` — ${why}` : ""}`;
    }
    case "health": {
      const drain = e.props?.["drainSec"];
      return `Capture health: ${level}${typeof drain === "number" ? `, ${drain.toFixed(1)} s of upload backlog` : ""}`;
    }
    case "gap":
      return `Not recorded for ${Math.round((e.durationMs ?? 0) / 1000)} s (${e.name ?? "unknown"})`;
    case "end":
      return `Recording ended (${e.name ?? "unknown"})`;
    default:
      return `control.${e.subtype ?? ""}`;
  }
}
